import React from 'react';
import { X } from 'lucide-react';
import type { HourlyWeatherSample, RouteWaypoint, UnitsMode, WeatherAlert } from '../types';
import {
  formatDistance,
  formatTempFull,
  formatVisibility,
  formatWind,
} from '../utils/units';
import { getWeatherLabel } from '../services/openMeteo';
import { isPlaceFallback } from '../services/places';

type TickerWeatherDetailProps = {
  waypoint: RouteWaypoint;
  weather?: HourlyWeatherSample;
  weatherLoading?: boolean;
  alerts?: WeatherAlert[];
  unitsMode: UnitsMode;
  isNarrow: boolean;
  onClose: () => void;
  lookingUpLocation?: boolean;
};

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="ticker-detail-metric">
      <div className="t-cap c2">{label}</div>
      <div className="t-headline">{value}</div>
    </div>
  );
}

function locationLabel(waypoint: RouteWaypoint, unitsMode: UnitsMode, lookingUp: boolean): string {
  const named = waypoint.placeName?.trim();
  if (named && !isPlaceFallback(named)) return named;
  if (lookingUp) return 'Looking up location…';
  if (named && !/^along route\b/i.test(named)) return named;
  const dist = formatDistance(waypoint.distanceM, unitsMode).primary;
  return `Along route · ${dist}`;
}

export const TickerWeatherDetail: React.FC<TickerWeatherDetailProps> = ({
  waypoint,
  weather,
  weatherLoading = false,
  alerts = [],
  unitsMode,
  isNarrow,
  onClose,
  lookingUpLocation = false,
}) => {
  const title = locationLabel(waypoint, unitsMode, lookingUpLocation);
  const condition = weather?.weatherCode != null ? getWeatherLabel(weather.weatherCode) : '—';
  const chance =
    weather?.precipProbability == null ? '—' : `${Math.round(weather.precipProbability)}% chance`;

  const body = (
    <div className="ticker-detail-card" role="dialog" aria-label={`Weather at ${title}`}>
      <div className="ticker-detail-head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="t-headline" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </div>
          <div className="t-foot c2">
            {!weather && weatherLoading ? 'Loading weather…' : weather ? condition : 'Weather unavailable'}
          </div>
        </div>
        <button className="cbtn" type="button" onClick={onClose} aria-label="Close weather detail">
          <X size={16} />
        </button>
      </div>
      <div className="ticker-detail-grid">
        <Metric label="Temperature" value={formatTempFull(weather?.temperatureC, unitsMode)} />
        <Metric label="Feels like" value={formatTempFull(weather?.apparentTemperatureC, unitsMode)} />
        <Metric label="Wind" value={formatWind(weather?.windKmh, unitsMode)} />
        <Metric label="Gusts" value={formatWind(weather?.windGustsKmh, unitsMode)} />
        <Metric label="Chance of precipitation" value={chance} />
        <Metric label="Visibility" value={formatVisibility(weather?.visibility, unitsMode)} />
      </div>
      {alerts[0] && (
        <div className="ticker-detail-alert" role="status">
          {alerts[0].headline || alerts[0].event}
        </div>
      )}
    </div>
  );

  if (isNarrow) {
    return (
      <div className="ticker-detail-dock" id="ticker-weather-detail">
        {body}
      </div>
    );
  }

  return (
    <div className="ticker-detail-float" id="ticker-weather-detail">
      {body}
    </div>
  );
};
